import React, { type ReactElement, type ReactNode } from 'react';
import { faCircleXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon, type FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { Link as ReactRouterLink } from 'react-router-dom';
import styles from './index.module.scss';

type SharedButtonAndLinkProps = {
  disabled?: boolean;
  children: ReactNode;
};

type SharedButtonOnlyProps = {
  onClick: () => void;
};

type ClearButtonProps = SharedButtonAndLinkProps & SharedButtonOnlyProps;

function ClearButton({ onClick, disabled, children }: ClearButtonProps): ReactElement {
  return (
    <button
      type="button"
      className="btn btn-outline-light text-muted me-2"
      disabled={disabled}
      onClick={onClick}
    >
      {children} <FontAwesomeIcon icon={faCircleXmark} className="ms-2 text-danger" size="lg" />
    </button>
  );
}

const sharedButtonAndLinkClasses = 'btn btn-outline-secondary';

type LinkProps = {
  href: string;
} & SharedButtonAndLinkProps &
  FontAwesomeIconProps;

function Link({ href, disabled, children, icon }: LinkProps): ReactElement {
  const iconElement = <FontAwesomeIcon icon={icon} className="me-2" />;

  if (disabled) {
    return (
      // If disabled is true, we should remove the href attribute, regardless of whether it is
      // passed as a prop, to prevent the link from functioning. The href attribute may be omitted
      // (as of HTML5) to create a placeholder link that resembles a traditional hyperlink, but does
      // not lead anywhere.
      // https://html.spec.whatwg.org/multipage/links.html#attr-hyperlink-href
      // https://w3c.github.io/html-aria/#example-communicate-a-disabled-link-with-aria
      // eslint-disable-next-line jsx-a11y/anchor-is-valid
      <a className={clsx(sharedButtonAndLinkClasses, { disabled })} role="link" aria-disabled>
        {iconElement} {children}
      </a>
    );
  }
  return (
    <ReactRouterLink to={href} className={sharedButtonAndLinkClasses}>
      {iconElement} {children}
    </ReactRouterLink>
  );
}

type ButtonProps = SharedButtonAndLinkProps & SharedButtonOnlyProps & FontAwesomeIconProps;

function Button({ onClick, disabled, children, icon }: ButtonProps): ReactElement {
  return (
    <button
      type="button"
      className={clsx(sharedButtonAndLinkClasses, { disabled })}
      disabled={disabled}
      onClick={onClick}
    >
      <FontAwesomeIcon icon={icon} className="me-2" /> {children}
    </button>
  );
}

type TableButtonsProps = {
  children: ReactNode;
  leftButtons?: boolean;
};

function TableButtons({ children, leftButtons }: TableButtonsProps): ReactElement {
  return (
    <span className={clsx(styles.tableButtons, { 'float-end': !leftButtons })}>{children}</span>
  );
}

export default Object.assign(TableButtons, {
  ClearButton,
  Link,
  Button
});
